import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { ArrowLeft, Save, Loader2, Package } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
});

const ITEM_TYPE_OPTIONS = [
  'Data Logger',
  'Temperature Control Box',
  'UN Approved Box',
  'Mapping Report'
];

const FormLabel = ({ children, required }) => (
  <Label className="text-xs font-semibold text-text-secondary uppercase tracking-wider font-ibm mb-1 block">
    {children}{required && <span className="text-accent-red ml-1">*</span>}
  </Label>
);

export default function InventoryForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);

  const [form, setForm] = useState({
    // Common fields
    item_type: '',
    sr_no: '',
    manufacture: '',
    purchase_date: '',
    expiry_date: '',
    validation_date: '',
    location: '',
    
    // Data Logger fields
    dl_type: '',
    dl_temp_range: '',
    dl_usage_type: '',
    dl_alpha_numeric: '',
    
    // Temperature Control Box fields
    tcb_model: '',
    tcb_type: '',
    tcb_temp_range: '',
    tcb_duration: '',
    tcb_size: '',
    tcb_outer_dim: '',
    tcb_inner_dim: '',
    tcb_gr_wt: '',
    tcb_ch_wt: '',
    
    // UN Approved Box fields
    uab_size: '',
    uab_external_dim: '',
    uab_inner_dim: '',
    uab_weight: '',
    uab_ch_wt: '',
    
    // Mapping Report fields
    mr_vehicle_equipment_no: '',
    mr_temp_range: '',
    mr_mapping_date: '',
    mr_expiry_date: '',
    mr_calibration_lab_name: '',
  });

  // Load item for edit
  useEffect(() => {
    if (!isEdit) return;
    setLoading(true);
    fetch(`${API}/api/inventory/${id}`, { headers: authHeaders() })
      .then(r => r.json())
      .then(data => {
        setForm({
          item_type: data.item_type || '',
          sr_no: data.sr_no || '',
          manufacture: data.manufacture || '',
          purchase_date: data.purchase_date || '',
          expiry_date: data.expiry_date || '',
          validation_date: data.validation_date || '',
          location: data.location || '',
          
          // Data Logger
          dl_type: data.data_logger_detail?.type || '',
          dl_temp_range: data.data_logger_detail?.temp_range || '',
          dl_usage_type: data.data_logger_detail?.usage_type || '',
          dl_alpha_numeric: data.data_logger_detail?.alpha_numeric || '',
          
          // Temp Control Box
          tcb_model: data.temp_control_box_detail?.model || '',
          tcb_type: data.temp_control_box_detail?.type || '',
          tcb_temp_range: data.temp_control_box_detail?.temp_range || '',
          tcb_duration: data.temp_control_box_detail?.duration || '',
          tcb_size: data.temp_control_box_detail?.size || '',
          tcb_outer_dim: data.temp_control_box_detail?.outer_dim || '',
          tcb_inner_dim: data.temp_control_box_detail?.inner_dim || '',
          tcb_gr_wt: data.temp_control_box_detail?.gr_wt || '',
          tcb_ch_wt: data.temp_control_box_detail?.ch_wt || '',
          
          // UN Approved Box
          uab_size: data.un_approved_box_detail?.size || '',
          uab_external_dim: data.un_approved_box_detail?.external_dim || '',
          uab_inner_dim: data.un_approved_box_detail?.inner_dim || '',
          uab_weight: data.un_approved_box_detail?.weight || '',
          uab_ch_wt: data.un_approved_box_detail?.ch_wt || '',
          
          // Mapping Report
          mr_vehicle_equipment_no: data.mapping_report_detail?.vehicle_equipment_no || '',
          mr_temp_range: data.mapping_report_detail?.temp_range || '',
          mr_mapping_date: data.mapping_report_detail?.mapping_date || '',
          mr_expiry_date: data.mapping_report_detail?.expiry_date || '',
          mr_calibration_lab_name: data.mapping_report_detail?.calibration_lab_name || '',
        });
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [isEdit, id]);

  const f = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));
  const sel = (field) => (val) => setForm(prev => ({ ...prev, [field]: val }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        item_type: form.item_type,
        sr_no: form.sr_no,
        manufacture: form.manufacture,
        purchase_date: form.purchase_date || null,
        expiry_date: form.expiry_date || null,
        validation_date: form.validation_date || null,
        location: form.location,
      };

      // Add type-specific detail based on item_type
      if (form.item_type === 'Data Logger') {
        payload.data_logger_detail = {
          type: form.dl_type,
          temp_range: form.dl_temp_range,
          usage_type: form.dl_usage_type,
          alpha_numeric: form.dl_alpha_numeric,
        };
      } else if (form.item_type === 'Temperature Control Box') {
        payload.temp_control_box_detail = {
          model: form.tcb_model,
          type: form.tcb_type,
          temp_range: form.tcb_temp_range,
          duration: form.tcb_duration,
          size: form.tcb_size,
          outer_dim: form.tcb_outer_dim,
          inner_dim: form.tcb_inner_dim,
          gr_wt: form.tcb_gr_wt,
          ch_wt: form.tcb_ch_wt,
        };
      } else if (form.item_type === 'UN Approved Box') {
        payload.un_approved_box_detail = {
          size: form.uab_size,
          external_dim: form.uab_external_dim,
          inner_dim: form.uab_inner_dim,
          weight: form.uab_weight,
          ch_wt: form.uab_ch_wt,
        };
      } else if (form.item_type === 'Mapping Report') {
        payload.mapping_report_detail = {
          vehicle_equipment_no: form.mr_vehicle_equipment_no,
          temp_range: form.mr_temp_range,
          mapping_date: form.mr_mapping_date || null,
          expiry_date: form.mr_expiry_date || null,
          calibration_lab_name: form.mr_calibration_lab_name,
        };
      }

      const url = isEdit ? `${API}/api/inventory/${id}` : `${API}/api/inventory`;
      const method = isEdit ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers: authHeaders(), body: JSON.stringify(payload) });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Save failed');
      }
      navigate('/inventory');
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-accent-blue" />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button type="button" variant="ghost" onClick={() => navigate('/inventory')} className="h-8 w-8 p-0">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-text-dark font-manrope">
              {isEdit ? 'Edit Inventory Item' : 'Add Inventory Item'}
            </h1>
          </div>
        </div>
        <Button type="submit" disabled={saving} className="bg-accent-blue hover:bg-accent-blue/90">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          {saving ? 'Saving...' : 'Save Item'}
        </Button>
      </div>

      {/* Common Fields */}
      <Card className="p-6">
        <div className="flex items-center gap-2 mb-5">
          <Package className="w-4 h-4 text-accent-blue" />
          <h3 className="text-base font-semibold text-text-dark font-manrope">Common Details</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <FormLabel required>Item Type</FormLabel>
            <Select value={form.item_type || '__none__'} onValueChange={sel('item_type')} required>
              <SelectTrigger className="font-ibm">
                <SelectValue placeholder="Select item type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__" disabled>Select item type</SelectItem>
                {ITEM_TYPE_OPTIONS.map(type => (
                  <SelectItem key={type} value={type}>{type}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <FormLabel required>SR # (Serial Number)</FormLabel>
            <Input value={form.sr_no} onChange={f('sr_no')} className="font-ibm" placeholder="e.g., DL-2024-001" required />
          </div>
          <div>
            <FormLabel>Manufacture</FormLabel>
            <Input value={form.manufacture} onChange={f('manufacture')} className="font-ibm" placeholder="Manufacturer name" />
          </div>
          <div>
            <FormLabel>Purchase Date</FormLabel>
            <Input type="date" value={form.purchase_date} onChange={f('purchase_date')} className="font-ibm" />
          </div>
          <div>
            <FormLabel>Expiry Date</FormLabel>
            <Input type="date" value={form.expiry_date} onChange={f('expiry_date')} className="font-ibm" />
          </div>
          <div>
            <FormLabel>Validation Date</FormLabel>
            <Input type="date" value={form.validation_date} onChange={f('validation_date')} className="font-ibm" />
          </div>
          <div className="md:col-span-3">
            <FormLabel>Location</FormLabel>
            <Input value={form.location} onChange={f('location')} className="font-ibm" placeholder="Storage location" />
          </div>
        </div>
      </Card>

      {/* Data Logger Fields */}
      {form.item_type === 'Data Logger' && (
        <Card className="p-6 bg-blue-50/30 border-accent-blue/30">
          <h3 className="text-base font-semibold text-text-dark font-manrope mb-5">Data Logger Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <FormLabel>Type</FormLabel>
              <Select value={form.dl_type || '__none__'} onValueChange={sel('dl_type')}>
                <SelectTrigger className="font-ibm">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Select type</SelectItem>
                  <SelectItem value="Single">Single</SelectItem>
                  <SelectItem value="Multi">Multi</SelectItem>
                  <SelectItem value="Single Display">Single Display</SelectItem>
                  <SelectItem value="Multi Display">Multi Display</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <FormLabel>Temp Range</FormLabel>
              <Input value={form.dl_temp_range} onChange={f('dl_temp_range')} className="font-ibm" placeholder="e.g., -20°C to +8°C" />
            </div>
            <div>
              <FormLabel>Usage Type</FormLabel>
              <Select value={form.dl_usage_type || '__none__'} onValueChange={sel('dl_usage_type')}>
                <SelectTrigger className="font-ibm">
                  <SelectValue placeholder="Select usage type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Select usage type</SelectItem>
                  <SelectItem value="Plus">Plus</SelectItem>
                  <SelectItem value="Minus">Minus</SelectItem>
                  <SelectItem value="Dry Ice">Dry Ice</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <FormLabel>Alpha + Numeric</FormLabel>
              <Input value={form.dl_alpha_numeric} onChange={f('dl_alpha_numeric')} className="font-ibm" placeholder="Alpha-numeric code" />
            </div>
          </div>
        </Card>
      )}

      {/* Temperature Control Box Fields */}
      {form.item_type === 'Temperature Control Box' && (
        <Card className="p-6 bg-green-50/30 border-green-500/30">
          <h3 className="text-base font-semibold text-text-dark font-manrope mb-5">Temperature Control Box Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <FormLabel>Model</FormLabel>
              <Input value={form.tcb_model} onChange={f('tcb_model')} className="font-ibm" placeholder="Model name" />
            </div>
            <div>
              <FormLabel>Type</FormLabel>
              <Select value={form.tcb_type || '__none__'} onValueChange={sel('tcb_type')}>
                <SelectTrigger className="font-ibm">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Select type</SelectItem>
                  <SelectItem value="Multiuse">Multiuse</SelectItem>
                  <SelectItem value="One Time">One Time</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <FormLabel>Temp Range</FormLabel>
              <Input value={form.tcb_temp_range} onChange={f('tcb_temp_range')} className="font-ibm" placeholder="e.g., 2°C to 8°C" />
            </div>
            <div>
              <FormLabel>Duration</FormLabel>
              <Input value={form.tcb_duration} onChange={f('tcb_duration')} className="font-ibm" placeholder="e.g., 48 hours" />
            </div>
            <div>
              <FormLabel>Size</FormLabel>
              <Input value={form.tcb_size} onChange={f('tcb_size')} className="font-ibm" placeholder="Box size" />
            </div>
            <div>
              <FormLabel>Outer Dim (in CMS)</FormLabel>
              <Input value={form.tcb_outer_dim} onChange={f('tcb_outer_dim')} className="font-ibm" placeholder="LxWxH" />
            </div>
            <div>
              <FormLabel>Inner Dim (in CMS)</FormLabel>
              <Input value={form.tcb_inner_dim} onChange={f('tcb_inner_dim')} className="font-ibm" placeholder="LxWxH" />
            </div>
            <div>
              <FormLabel>G.RW.T</FormLabel>
              <Input value={form.tcb_gr_wt} onChange={f('tcb_gr_wt')} className="font-ibm" placeholder="Gross weight" />
            </div>
            <div>
              <FormLabel>CH.WT.</FormLabel>
              <Input value={form.tcb_ch_wt} onChange={f('tcb_ch_wt')} className="font-ibm" placeholder="Chargeable weight" />
            </div>
          </div>
        </Card>
      )}

      {/* UN Approved Box Fields */}
      {form.item_type === 'UN Approved Box' && (
        <Card className="p-6 bg-orange-50/30 border-orange-500/30">
          <h3 className="text-base font-semibold text-text-dark font-manrope mb-5">UN Approved Box Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <FormLabel>Size</FormLabel>
              <Input value={form.uab_size} onChange={f('uab_size')} className="font-ibm" placeholder="Box size" />
            </div>
            <div>
              <FormLabel>External Dim (CMS / Inch)</FormLabel>
              <Input value={form.uab_external_dim} onChange={f('uab_external_dim')} className="font-ibm" placeholder="LxWxH" />
            </div>
            <div>
              <FormLabel>Inner Dim (CMS / Inch)</FormLabel>
              <Input value={form.uab_inner_dim} onChange={f('uab_inner_dim')} className="font-ibm" placeholder="LxWxH" />
            </div>
            <div>
              <FormLabel>Weight</FormLabel>
              <Input value={form.uab_weight} onChange={f('uab_weight')} className="font-ibm" placeholder="Weight" />
            </div>
            <div>
              <FormLabel>CH.WT.</FormLabel>
              <Input value={form.uab_ch_wt} onChange={f('uab_ch_wt')} className="font-ibm" placeholder="Chargeable weight" />
            </div>
          </div>
        </Card>
      )}

      {/* Mapping Report Fields */}
      {form.item_type === 'Mapping Report' && (
        <Card className="p-6 bg-purple-50/30 border-purple-500/30">
          <h3 className="text-base font-semibold text-text-dark font-manrope mb-5">Mapping Report Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <FormLabel>Vehicle / Equipment #</FormLabel>
              <Input value={form.mr_vehicle_equipment_no} onChange={f('mr_vehicle_equipment_no')} className="font-ibm" placeholder="Vehicle number" />
            </div>
            <div>
              <FormLabel>Temperature Range</FormLabel>
              <Input value={form.mr_temp_range} onChange={f('mr_temp_range')} className="font-ibm" placeholder="Temp range" />
            </div>
            <div>
              <FormLabel>Mapping Date</FormLabel>
              <Input type="date" value={form.mr_mapping_date} onChange={f('mr_mapping_date')} className="font-ibm" />
            </div>
            <div>
              <FormLabel>Expiry Date</FormLabel>
              <Input type="date" value={form.mr_expiry_date} onChange={f('mr_expiry_date')} className="font-ibm" />
            </div>
            <div className="md:col-span-2">
              <FormLabel>Calibration Lab Name</FormLabel>
              <Input value={form.mr_calibration_lab_name} onChange={f('mr_calibration_lab_name')} className="font-ibm" placeholder="Lab name" />
            </div>
          </div>
        </Card>
      )}

      {/* Upload Documents placeholder */}
      <Card className="p-6">
        <h3 className="text-base font-semibold text-text-dark font-manrope mb-3">Upload Documents</h3>
        <p className="text-sm text-text-secondary">Document upload functionality can be added here (optional for MVP).</p>
      </Card>

      {/* Bottom Save */}
      <div className="flex justify-end gap-3">
        <Button type="button" variant="outline" onClick={() => navigate('/inventory')}>
          Cancel
        </Button>
        <Button type="submit" disabled={saving} className="bg-accent-blue hover:bg-accent-blue/90">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          {saving ? 'Saving...' : isEdit ? 'Update Item' : 'Create Item'}
        </Button>
      </div>
    </form>
  );
}
